/**
 * Admin Analytics Dashboard Component
 * Real-time traffic, user stats, revenue, and popular pages
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import {
  Users, UserCheck, TrendingUp, Eye, Activity,
  DollarSign, FileText, Calendar, RefreshCw, Clock,
  ArrowUpRight, ArrowDownRight, Globe
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'blue' }) => {
  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    purple: 'bg-purple-500',
    orange: 'bg-orange-500',
    red: 'bg-red-500',
    teal: 'bg-teal-500'
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{value}</p>
          {subtitle && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{subtitle}</p>
          )}
          {trend && (
            <div className={`flex items-center mt-2 text-sm ${trend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
              {trend === 'up' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              <span>{trendValue}</span>
            </div>
          )}
        </div>
        <div className={`${colorClasses[color]} p-4 rounded-xl`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );
};

const RealtimeIndicator = ({ count }) => (
  <div className="flex items-center gap-2">
    <span className="relative flex h-3 w-3">
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
      <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
    </span>
    <span className="text-green-600 dark:text-green-400 font-semibold">{count} visiteur{count > 1 ? 's' : ''} en ligne</span>
  </div>
);

const AdminAnalyticsDashboard = () => {
  const [dashboardStats, setDashboardStats] = useState(null);
  const [realtimeTraffic, setRealtimeTraffic] = useState(null);
  const [trafficHistory, setTrafficHistory] = useState([]);
  const [usersHistory, setUsersHistory] = useState([]);
  const [revenueHistory, setRevenueHistory] = useState([]);
  const [popularPages, setPopularPages] = useState([]);
  const [subscriptionStats, setSubscriptionStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const adminToken = localStorage.getItem('adminToken');
  
  const headers = {
    'Authorization': `Bearer ${adminToken}`,
    'Content-Type': 'application/json'
  };

  const fetchDashboardStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/analytics/dashboard`, { headers });
      if (response.ok) {
        const data = await response.json();
        setDashboardStats(data);
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
  }, [adminToken]);

  const fetchRealtimeTraffic = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/analytics/traffic/realtime`, { headers });
      if (response.ok) {
        const data = await response.json();
        setRealtimeTraffic(data);
      }
    } catch (error) {
      console.error('Error fetching realtime traffic:', error);
    }
  }, [adminToken]);

  const fetchTrafficHistory = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/analytics/traffic/history?days=30`, { headers });
      if (response.ok) {
        const data = await response.json();
        setTrafficHistory(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching traffic history:', error);
    }
  }, [adminToken]);

  const fetchUsersHistory = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/analytics/users/history?days=30`, { headers });
      if (response.ok) {
        const data = await response.json();
        setUsersHistory(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching users history:', error);
    }
  }, [adminToken]);

  const fetchRevenueHistory = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/analytics/revenue/history?days=30`, { headers });
      if (response.ok) {
        const data = await response.json();
        setRevenueHistory(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching revenue history:', error);
    }
  }, [adminToken]);

  const fetchPopularPages = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/analytics/pages/popular?days=30&limit=10`, { headers });
      if (response.ok) {
        const data = await response.json();
        setPopularPages(data.pages || []);
      }
    } catch (error) {
      console.error('Error fetching popular pages:', error);
    }
  }, [adminToken]);

  const fetchSubscriptionStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/analytics/subscriptions/stats`, { headers });
      if (response.ok) {
        const data = await response.json();
        setSubscriptionStats(data);
      }
    } catch (error) {
      console.error('Error fetching subscription stats:', error);
    }
  }, [adminToken]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchDashboardStats(),
      fetchRealtimeTraffic(),
      fetchTrafficHistory(),
      fetchUsersHistory(),
      fetchRevenueHistory(),
      fetchPopularPages(),
      fetchSubscriptionStats()
    ]);
    setLastUpdate(new Date());
    setLoading(false);
  }, [fetchDashboardStats, fetchRealtimeTraffic, fetchTrafficHistory, fetchUsersHistory, fetchRevenueHistory, fetchPopularPages, fetchSubscriptionStats]);

  // Initial fetch
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Auto-refresh realtime data every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchRealtimeTraffic();
      fetchDashboardStats();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchRealtimeTraffic, fetchDashboardStats]);

  const formatPageName = (page) => {
    if (page === '/') return 'Accueil';
    return page.replace(/^\//, '').split('/').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' > ');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  };

  if (loading && !dashboardStats) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-3 text-gray-600 dark:text-gray-300">Chargement des analytics...</span>
      </div>
    );
  }

  const subscriptionPieData = subscriptionStats ? [
    { name: 'Actifs', value: subscriptionStats.active || 0 },
    { name: 'Essai', value: subscriptionStats.trialing || 0 },
    { name: 'Annulés', value: subscriptionStats.canceled || 0 },
    { name: 'En retard', value: subscriptionStats.past_due || 0 }
  ].filter(d => d.value > 0) : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">📊 Analytics</h2>
          <p className="text-gray-500 dark:text-gray-400">
            Vue d'ensemble de votre plateforme en temps réel
          </p>
        </div>
        <div className="flex items-center gap-4">
          {realtimeTraffic && (
            <RealtimeIndicator count={realtimeTraffic.active_visitors} />
          )}
          <button
            onClick={fetchAllData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
        </div>
      </div>

      {lastUpdate && (
        <p className="text-sm text-gray-400 flex items-center gap-1">
          <Clock className="w-4 h-4" />
          Dernière mise à jour : {lastUpdate.toLocaleTimeString('fr-FR')}
        </p>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Patients inscrits"
          value={dashboardStats?.users?.total_patients || 0}
          subtitle={`+${dashboardStats?.users?.new_patients_week || 0} cette semaine`}
          icon={Users}
          color="blue"
        />
        <StatCard
          title="Vétérinaires"
          value={dashboardStats?.users?.total_vets || 0}
          subtitle={`+${dashboardStats?.users?.new_vets_week || 0} cette semaine`}
          icon={UserCheck}
          color="green"
        />
        <StatCard
          title="Pages vues aujourd'hui"
          value={dashboardStats?.traffic?.views_today || 0}
          subtitle={`${dashboardStats?.traffic?.views_week || 0} cette semaine`}
          icon={Eye}
          color="purple"
        />
        <StatCard
          title="Revenus ce mois"
          value={formatCurrency(dashboardStats?.revenue?.this_month)}
          subtitle={`${dashboardStats?.revenue?.active_subscriptions || 0} abonnements actifs`}
          icon={DollarSign}
          color="orange"
        />
      </div>

      {/* Second Row Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="RDV aujourd'hui"
          value={dashboardStats?.appointments?.today || 0}
          subtitle={`${dashboardStats?.appointments?.this_week || 0} cette semaine`}
          icon={Calendar}
          color="teal"
        />
        <StatCard
          title="Visiteurs actifs"
          value={realtimeTraffic?.active_visitors || 0}
          subtitle="En temps réel"
          icon={Activity}
          color="green"
        />
        <StatCard
          title="Articles publiés"
          value={dashboardStats?.content?.published_articles || 0}
          subtitle={`${dashboardStats?.content?.total_articles || 0} au total`}
          icon={FileText}
          color="blue"
        />
        <StatCard
          title="En attente d'approbation"
          value={(dashboardStats?.users?.pending_vets || 0) + (dashboardStats?.users?.pending_patients || 0)}
          subtitle={`${dashboardStats?.users?.pending_vets || 0} vets, ${dashboardStats?.users?.pending_patients || 0} patients`}
          icon={Clock}
          color="red"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Traffic Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-500" />
            Évolution du trafic (30 jours)
          </h3>
          {trafficHistory.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={trafficHistory}>
                <defs>
                  <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={formatDate}
                  stroke="#9CA3AF"
                  fontSize={12}
                />
                <YAxis stroke="#9CA3AF" fontSize={12} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1F2937', 
                    border: 'none', 
                    borderRadius: '8px',
                    color: '#F9FAFB'
                  }}
                  labelFormatter={formatDate}
                />
                <Area 
                  type="monotone" 
                  dataKey="views" 
                  stroke="#3B82F6" 
                  fillOpacity={1} 
                  fill="url(#colorViews)"
                  name="Pages vues"
                />
                <Area 
                  type="monotone" 
                  dataKey="unique_visitors" 
                  stroke="#10B981" 
                  fillOpacity={0.3} 
                  fill="#10B981"
                  name="Visiteurs uniques"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-400">
              Pas de données de trafic disponibles
            </div>
          )}
        </div>

        {/* Users Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-green-500" />
            Inscriptions (30 jours)
          </h3>
          {usersHistory.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={usersHistory}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={formatDate}
                  stroke="#9CA3AF"
                  fontSize={12}
                />
                <YAxis stroke="#9CA3AF" fontSize={12} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1F2937', 
                    border: 'none', 
                    borderRadius: '8px',
                    color: '#F9FAFB'
                  }}
                  labelFormatter={formatDate}
                />
                <Bar dataKey="patients" fill="#3B82F6" name="Patients" radius={[4, 4, 0, 0]} />
                <Bar dataKey="veterinaires" fill="#10B981" name="Vétérinaires" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-400">
              Pas de données d'inscriptions disponibles
            </div>
          )}
        </div>
      </div>

      {/* Revenue and Pages Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-orange-500" />
            Revenus (30 jours)
          </h3>
          {revenueHistory.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={revenueHistory}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={formatDate}
                  stroke="#9CA3AF"
                  fontSize={12}
                />
                <YAxis 
                  stroke="#9CA3AF" 
                  fontSize={12}
                  tickFormatter={(value) => `${value}€`}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1F2937', 
                    border: 'none', 
                    borderRadius: '8px',
                    color: '#F9FAFB'
                  }}
                  labelFormatter={formatDate}
                  formatter={(value) => [formatCurrency(value), 'Revenus']}
                />
                <Line 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="#F59E0B" 
                  strokeWidth={3}
                  dot={{ fill: '#F59E0B', strokeWidth: 2 }}
                  name="Revenus"
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-400">
              Pas de données de revenus disponibles
            </div>
          )}
        </div>

        {/* Popular Pages */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-purple-500" />
            Pages les plus visitées (30 jours)
          </h3>
          {popularPages.length > 0 ? (
            <div className="space-y-3 max-h-[300px] overflow-y-auto">
              {popularPages.map((page, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <span className={`
                      w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                      ${index === 0 ? 'bg-yellow-100 text-yellow-700' : 
                        index === 1 ? 'bg-gray-200 text-gray-700' :
                        index === 2 ? 'bg-orange-100 text-orange-700' :
                        'bg-blue-100 text-blue-700'}
                    `}>
                      {index + 1}
                    </span>
                    <span className="text-gray-700 dark:text-gray-200 font-medium truncate max-w-[200px]">
                      {formatPageName(page.page)}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-900 dark:text-white font-semibold">{page.views} vues</p>
                    <p className="text-xs text-gray-500">{page.unique_visitors} visiteurs</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-400">
              Pas de données de pages disponibles
            </div>
          )}
        </div>
      </div>

      {/* Subscriptions and Realtime Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Subscription Distribution */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-green-500" />
            Répartition des abonnements
          </h3>
          {subscriptionPieData.length > 0 ? (
            <div className="flex items-center">
              <ResponsiveContainer width="50%" height={200}>
                <PieChart>
                  <Pie
                    data={subscriptionPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {subscriptionPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1F2937', 
                      border: 'none', 
                      borderRadius: '8px',
                      color: '#F9FAFB'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2">
                {subscriptionPieData.map((entry, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <span className="text-gray-600 dark:text-gray-300">{entry.name}</span>
                    <span className="font-semibold text-gray-900 dark:text-white">{entry.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-gray-400">
              Aucun abonnement
            </div>
          )}
        </div>

        {/* Realtime Activity */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-green-500" />
            Activité en temps réel
          </h3>
          {realtimeTraffic?.current_pages && Object.keys(realtimeTraffic.current_pages).length > 0 ? (
            <div className="space-y-3 max-h-[200px] overflow-y-auto">
              {Object.entries(realtimeTraffic.current_pages)
                .sort(([,a], [,b]) => b - a)
                .map(([page, count], index) => (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-100 dark:border-green-800"
                  >
                    <div className="flex items-center gap-2">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                      </span>
                      <span className="text-gray-700 dark:text-gray-200">{formatPageName(page)}</span>
                    </div>
                    <span className="font-semibold text-green-600 dark:text-green-400">{count} actif{count > 1 ? 's' : ''}</span>
                  </div>
                ))}
            </div>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-gray-400">
              <div className="text-center">
                <Globe className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Aucun visiteur actif</p>
                <p className="text-sm">Les visiteurs apparaîtront ici en temps réel</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminAnalyticsDashboard;
